// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * modes.ts — three races with genuine spread (principle #14). A mode changes how
 * the race PLAYS, not a single number:
 *
 *   - Sprint   4 stops, reliable info, honest driver. The on-ramp: telemetry
 *              tells you almost everything, so triage is a clean read.
 *   - Enduro   7 stops, noisier info, wear that compounds over a long race —
 *              fuel and tyre management become the spine, small mistakes bank up.
 *   - Monsoon  5 stops, sensors frequently DARK and a driver who panics and is
 *              often wrong. Here the radio is the only signal you have on the
 *              system that is quietly about to blow, so weighing it is the game.
 *
 * The mode is the HOST's pick and travels frozen inside the round start
 * (main.ts's roundOpts), because it changes the race's shape and length — two
 * peers reading their own picker would be racing different seasons on one ladder.
 * An id off the wire is always validated through `modeOf`, never trusted raw.
 */

export interface Mode {
  id: string;
  name: string;
  blurb: string;
  /** Number of pit stops in the race. */
  stops: number;
  /** Multiplier on per-stint wear. */
  wearScale: number;
  /** Stddev-ish of telemetry noise added to a reading. */
  teleNoise: number;
  /** Base chance a system's sensor is offline at a stop. */
  teleOffline: number;
  /** Chance the driver flags the truly-worst system. */
  radioHonest: number;
  /** Chance the driver says nothing at all. */
  radioSilent: number;
  /** Chance of an incident spike (wing/brakes) per stint. */
  incident: number;
}

export const MODES: readonly Mode[] = [
  {
    id: 'sprint',
    name: 'Sprint',
    blurb: '4 stops · clear telemetry · straight read',
    stops: 4,
    wearScale: 1.0,
    teleNoise: 0.06,
    teleOffline: 0.03,
    radioHonest: 0.85,
    radioSilent: 0.22,
    incident: 0.14,
  },
  {
    id: 'enduro',
    name: 'Enduro',
    blurb: '7 stops · noisier data · the mistakes compound',
    stops: 7,
    wearScale: 1.08,
    teleNoise: 0.1,
    teleOffline: 0.06,
    radioHonest: 0.8,
    radioSilent: 0.18,
    incident: 0.18,
  },
  {
    id: 'monsoon',
    name: 'Monsoon',
    blurb: '5 stops · sensors dark · trust the radio',
    stops: 5,
    wearScale: 1.12,
    teleNoise: 0.14,
    teleOffline: 0.24,
    radioHonest: 0.72,
    radioSilent: 0.12,
    incident: 0.3,
  },
];

export const DEFAULT_MODE: Mode = MODES[0];

/** Validate an id off the wire or storage; unknown falls back, never undefined. */
export function modeOf(id: string | null | undefined): Mode {
  return MODES.find((m) => m.id === id) ?? DEFAULT_MODE;
}
